import { ActionIcon, Button, Loader, Select, TextInput } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconLayoutGrid,
  IconList,
  IconLock,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Item } from "./data";
import { categoryLabel } from "./labels";

interface ItemGridProps {
  items: Item[];
  selectedItem: Item | null;
  loading?: boolean;
  onSelectItem: (item: Item) => void;
  onAddItem: () => void;
}

function ItemGrid({
  items,
  selectedItem,
  loading,
  onSelectItem,
  onAddItem,
}: ItemGridProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        (item.spec ?? "").toLowerCase().includes(query),
    );
  }, [items, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-gray-50">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white p-4">
        <div className="max-w-xl flex-1">
          <TextInput
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.currentTarget.value);
              setCurrentPage(1);
            }}
            placeholder={t("itemEditor.grid.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
          />
        </div>
        <Button
          variant="default"
          leftSection={<IconFilter size={16} />}
        >
          {t("itemEditor.grid.filter")}
        </Button>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={onAddItem}
        >
          {t("itemEditor.grid.addItem")}
        </Button>
        <div className="flex overflow-hidden rounded-lg border border-gray-200">
          <ActionIcon
            variant={viewMode === "grid" ? "filled" : "subtle"}
            color={viewMode === "grid" ? "blue" : "gray"}
            size="lg"
            radius={0}
            onClick={() => setViewMode("grid")}
          >
            <IconLayoutGrid size={16} />
          </ActionIcon>
          <ActionIcon
            variant={viewMode === "list" ? "filled" : "subtle"}
            color={viewMode === "list" ? "blue" : "gray"}
            size="lg"
            radius={0}
            onClick={() => setViewMode("list")}
          >
            <IconList size={16} />
          </ActionIcon>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader />
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {paginatedItems.map((item) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  className={`rounded-xl border bg-white p-4 text-left transition-all hover:shadow-md ${
                    isSelected
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <div className="relative mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-gray-100 to-gray-200">
                    <span className="text-3xl text-gray-400">📦</span>
                    {item.isPreset && (
                      <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-gray-900/80 px-2 py-0.5 text-xs text-white">
                        <IconLock size={12} />
                        {t("itemEditor.grid.preset")}
                      </span>
                    )}
                  </div>
                  <h3 className="truncate text-sm font-medium text-gray-900">
                    {item.name}
                  </h3>
                  {item.spec && (
                    <p className="mt-1 text-xs text-gray-500">{item.spec}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                      {categoryLabel(t, item.categoryId, item.category)}
                    </span>
                    {item.steelType && (
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {item.steelType}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            <button
              onClick={onAddItem}
              className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white p-4 text-center transition-all hover:border-blue-500 hover:bg-blue-50/50"
            >
              <IconPlus size={32} className="mb-2 text-gray-400" />
              <span className="text-sm text-gray-500">{t("itemEditor.grid.addItem")}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedItems.map((item) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  className={`flex w-full items-center gap-4 rounded-lg border bg-white p-4 text-left transition-all hover:shadow-md ${
                    isSelected
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    <span className="text-2xl">📦</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium text-gray-900">
                      {item.name}
                    </h3>
                    {item.spec && (
                      <p className="text-sm text-gray-500">{item.spec}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.isPreset && (
                      <span className="flex flex-shrink-0 items-center gap-1 rounded bg-gray-900/80 px-2 py-0.5 text-xs text-white">
                        <IconLock size={12} />
                        {t("itemEditor.grid.preset")}
                      </span>
                    )}
                    <span className="flex-shrink-0 rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                      {categoryLabel(t, item.categoryId, item.category)}
                    </span>
                    {item.steelType && (
                      <span className="flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {item.steelType}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 bg-white p-4">
        <span className="text-sm text-gray-500">
          {t("itemEditor.grid.total", { total: filteredItems.length })}
        </span>
        <div className="flex items-center gap-2">
          <ActionIcon
            variant="default"
            size="md"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
          {getPageNumbers().map((page, idx) =>
            typeof page === "number" ? (
              <ActionIcon
                key={idx}
                variant={currentPage === page ? "filled" : "default"}
                color={currentPage === page ? "blue" : "gray"}
                size="md"
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </ActionIcon>
            ) : (
              <span key={idx} className="px-1 text-gray-500">
                {page}
              </span>
            ),
          )}
          <ActionIcon
            variant="default"
            size="md"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(itemsPerPage)}
            onChange={(v) => {
              if (v) {
                setItemsPerPage(Number(v));
                setCurrentPage(1);
              }
            }}
            data={[
              { value: "12", label: t("itemEditor.grid.perPage", { size: 12 }) },
              { value: "24", label: t("itemEditor.grid.perPage", { size: 24 }) },
              { value: "48", label: t("itemEditor.grid.perPage", { size: 48 }) },
            ]}
            w={110}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
}

export default ItemGrid;
